import { useEffect, useMemo, useState } from 'react';
import { PALETTE } from '../palette.js';
import { api } from '../api.js';
import { Button, Card, Pill } from '../components/primitives.jsx';
import ProtectedPageLoader from '../components/ProtectedPageLoader.jsx';
import { IconCheck, IconChevron, IconInfo, IconPause, IconPlay, IconSettings } from '../components/icons.jsx';
import { safeFailureMessage } from '../safe-failure.js';
import FunnelGenerationApprovalForm from './FunnelGenerationApprovalForm.jsx';

const RETRYABLE = new Set(['failed', 'canceled']);
const CANCELABLE = new Set(['queued', 'running']);
// A funnel that loud-failed because its POC preset could not be resolved is
// recoverable in-product: supply the presentation, then retry the workflow.
const FUNNEL_PRESET_UNRESOLVED = 'funnel_generation_poc_preset_unresolved';

function tenantHeaders(companyId) {
  return companyId ? { headers: { 'x-company-id': companyId } } : undefined;
}

function titleCase(value) {
  return String(value || '')
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'Unknown';
}

function fmtDateTime(value) {
  if (!value) return '-';
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function isCancelPending(job) {
  return Boolean(job?.cancelRequested) && CANCELABLE.has(job?.status);
}

function statusTone(status, cancelRequested = false) {
  if (cancelRequested && CANCELABLE.has(status)) return 'warn';
  if (status === 'succeeded' || status === 'waiting_approval') return 'live';
  if (status === 'running') return 'accent';
  if (status === 'failed' || status === 'canceled') return 'warn';
  return 'neutral';
}

// Recovery is tenant-facing, so it uses the same pipeline-only vocabulary as
// every other customer surface: a workflow job rests at waiting_approval
// after any approval decision, and titleCase would render it "Waiting
// Approval" — review vocabulary that contradicts the campaign lifecycle.
export function recoveryStatusLabel(job) {
  if (isCancelPending(job)) return 'Canceling';
  const status = job?.status;
  if (status === 'succeeded' || status === 'waiting_approval') return 'Complete';
  return titleCase(status);
}

function workflowAttentionWeight(job) {
  if (job.status === 'failed') return 0;
  if (job.cancelRequested) return 1;
  if (job.status === 'running') return 2;
  if (job.status === 'queued') return 3;
  if (job.status === 'canceled') return 4;
  return 9;
}

export default function WorkflowRecovery({ session }) {
  const memberships = session.user?.memberships || [];
  const activeCompanyId = session.user?.activeCompany?.id || memberships[0]?.companyId || '';
  const isSaasSession = session.user?.auth?.type === 'supabase';
  const [policy, setPolicy] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [selectedId, setSelectedId] = useState('');
  const [auditLogs, setAuditLogs] = useState([]);
  const [status, setStatus] = useState('idle');
  const [actionStatus, setActionStatus] = useState('idle');
  const [error, setError] = useState(null);
  const [presentationForm, setPresentationForm] = useState(null);

  const selectedJob = jobs.find((job) => job.id === selectedId) || jobs[0] || null;
  const actions = policy?.actions || [];
  const canReadAudit = actions.includes('audit.read');
  const canRetry = actions.includes('workflows.retry');
  const canCancel = actions.includes('workflows.cancel');
  const showInitialRecoveryLoading =
    isSaasSession &&
    Boolean(activeCompanyId) &&
    !policy &&
    jobs.length === 0 &&
    status !== 'error';

  async function loadRecoveryData() {
    if (!isSaasSession || !activeCompanyId) return;
    setStatus('loading');
    setError(null);
    try {
      const [nextPolicy, workflowResponse] = await Promise.all([
        api.get('/api/saas/policy', tenantHeaders(activeCompanyId)),
        api.get('/api/saas/workflows?limit=100', tenantHeaders(activeCompanyId)),
      ]);
      const sorted = [...(workflowResponse.workflowJobs || [])].sort((a, b) => {
        const attention = workflowAttentionWeight(a) - workflowAttentionWeight(b);
        if (attention) return attention;
        return String(b.updatedAt || b.createdAt || '').localeCompare(String(a.updatedAt || a.createdAt || ''));
      });
      setPolicy(nextPolicy);
      setJobs(sorted);
      setSelectedId((current) => current && sorted.some((job) => job.id === current)
        ? current
        : sorted[0]?.id || '');
      setStatus('ready');
    } catch (err) {
      setError(err);
      setStatus('error');
    }
  }

  useEffect(() => {
    loadRecoveryData();
  }, [isSaasSession, activeCompanyId]);

  useEffect(() => {
    if (!activeCompanyId || !selectedJob?.id || !canReadAudit) {
      setAuditLogs([]);
      return;
    }

    let alive = true;
    api.get(
      `/api/saas/audit-logs?targetType=workflow_job&targetId=${encodeURIComponent(selectedJob.id)}&limit=12`,
      tenantHeaders(activeCompanyId),
    )
      .then((response) => {
        if (alive) setAuditLogs(response.auditLogs || []);
      })
      .catch(() => {
        if (alive) setAuditLogs([]);
      });

    return () => { alive = false; };
  }, [activeCompanyId, selectedJob?.id, canReadAudit]);

  const summary = useMemo(() => ({
    total: jobs.length,
    failed: jobs.filter((job) => job.status === 'failed').length,
    active: jobs.filter((job) => job.status === 'running' || job.status === 'queued').length,
    canceling: jobs.filter(isCancelPending).length,
  }), [jobs]);

  async function runAction(type) {
    if (!selectedJob || actionStatus !== 'idle') return;
    setActionStatus(type);
    setError(null);
    const path = `/api/saas/workflows/${selectedJob.id}/${type}`;
    try {
      await api.post(path, {
        reason: type === 'retry' ? 'admin_recovery_retry' : 'admin_recovery_cancel',
      }, tenantHeaders(activeCompanyId));
      await loadRecoveryData();
    } catch (err) {
      setError(err);
    } finally {
      setActionStatus('idle');
    }
  }

  if (!isSaasSession) {
    return (
      <PageShell>
        <Header />
        <EmptyState title="SaaS session unavailable" body="Sign in with a SaaS account to access workflow recovery." />
      </PageShell>
    );
  }

  if (showInitialRecoveryLoading) {
    return (
      <PageShell>
        <Header companyName={session.user?.activeCompany?.name} role={session.user?.role} />
        <ProtectedPageLoader />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Header companyName={session.user?.activeCompany?.name} role={policy?.role || session.user?.role} />

      <section style={summaryGridStyle}>
        <SummaryCard label="Needs attention" value={String(summary.failed)} tone="warn" />
        <SummaryCard label="Active" value={String(summary.active)} tone="accent" />
        <SummaryCard label="Cancel requested" value={String(summary.canceling)} tone="warn" />
        <SummaryCard label="Total workflows" value={String(summary.total)} tone="neutral" />
      </section>

      {error && (
        <div style={{ marginTop: 16 }}>
          <EmptyState compact tone="warn" title="Recovery request failed" body={safeFailureMessage(error, 'recovery')} />
        </div>
      )}

      <section style={mainGridStyle}>
        <Card style={panelStyle}>
          <SectionTitle icon={<IconSettings size={16} />} title="Workflow Queue" />
          {status === 'error' ? (
            <EmptyState compact tone="warn" title="Workflows unavailable" body={safeFailureMessage(error, 'recovery')} />
          ) : jobs.length === 0 ? (
            <EmptyState compact title="No workflow jobs" body="This company has no SaaS workflow jobs yet." />
          ) : (
            <div style={{ display: 'grid', gap: 8 }}>
              {jobs.map((job) => (
                <WorkflowRow
                  key={job.id}
                  job={job}
                  active={selectedJob?.id === job.id}
                  onClick={() => setSelectedId(job.id)}
                />
              ))}
            </div>
          )}
        </Card>

        <div style={{ display: 'grid', gap: 16, alignContent: 'start' }}>
          <Card style={panelStyle}>
            <SectionTitle icon={<IconInfo size={16} />} title="Recovery Detail" />
            {selectedJob ? (
              <WorkflowDetail
                job={selectedJob}
                canRetry={canRetry && RETRYABLE.has(selectedJob.status)}
                canCancel={canCancel && CANCELABLE.has(selectedJob.status) && !selectedJob.cancelRequested}
                onRetry={() => runAction('retry')}
                onCancel={() => runAction('cancel')}
                actionStatus={actionStatus}
                canProvidePresentation={canRetry && selectedJob.error?.code === FUNNEL_PRESET_UNRESOLVED && Boolean(selectedJob.campaignId)}
                onProvidePresentation={() => setPresentationForm({
                  campaignId: selectedJob.campaignId,
                })}
              />
            ) : (
              <EmptyState compact title="No workflow selected" body="Select a workflow job to inspect recovery detail." />
            )}
          </Card>

          <Card style={panelStyle}>
            <SectionTitle icon={<IconCheck size={16} />} title="Audit Trail" />
            {auditLogs.length === 0 ? (
              <EmptyState compact title="No audit rows" body="No workflow recovery audit events are visible for this job." />
            ) : (
              <div style={{ display: 'grid', gap: 8 }}>
                {auditLogs.map((entry) => (
                  <AuditRow key={entry.id} entry={entry} />
                ))}
              </div>
            )}
          </Card>
        </div>
      </section>

      {presentationForm && (
        <FunnelGenerationApprovalForm
          companyId={activeCompanyId}
          campaignId={presentationForm.campaignId}
          onClose={() => setPresentationForm(null)}
          onApproved={async () => {
            setPresentationForm(null);
            // Presentation supplied — retry the workflow so it regenerates on
            // the POC preset path instead of loud-failing again.
            await runAction('retry');
          }}
        />
      )}
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <div style={{ padding: 'clamp(18px, 4vw, 38px) clamp(16px, 4vw, 38px) 64px', maxWidth: 1240, margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
      {children}
    </div>
  );
}

function Header({ companyName, role }) {
  return (
    <header style={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between', gap: 18, marginBottom: 24, flexWrap: 'wrap' }}>
      <div>
        <div style={{ fontSize: 11, letterSpacing: 0, textTransform: 'uppercase', color: PALETTE.ink3, marginBottom: 6 }}>Recovery</div>
        <h1 style={{
          fontFamily: 'Instrument Serif, serif',
          fontStyle: 'italic',
          fontSize: 40,
          fontWeight: 400,
          margin: 0,
          color: PALETTE.ink,
          letterSpacing: 0,
        }}>
          Workflow Admin
        </h1>
        <div style={{ fontSize: 13, color: PALETTE.ink3, marginTop: 6 }}>{companyName || 'Active company'}</div>
      </div>
      <Pill tone={role === 'owner' || role === 'admin' ? 'accent' : 'neutral'}>{titleCase(role)}</Pill>
    </header>
  );
}

function SectionTitle({ icon, title }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: PALETTE.ink, fontSize: 14, fontWeight: 600, marginBottom: 16 }}>
      <span style={{ color: PALETTE.accent }}>{icon}</span>
      {title}
    </div>
  );
}

function SummaryCard({ label, value, tone }) {
  const color = tone === 'warn' ? PALETTE.warn : tone === 'accent' ? PALETTE.accent : PALETTE.ink;
  return (
    <div style={{
      padding: '18px 20px',
      background: PALETTE.surface,
      border: `1px solid ${PALETTE.line}`,
      borderRadius: 10,
      minWidth: 0,
    }}>
      <div style={{ fontSize: 11, color: PALETTE.ink3, letterSpacing: 0, textTransform: 'uppercase', marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: 'Instrument Serif, serif', fontSize: 38, lineHeight: 1, color }}>{value}</div>
    </div>
  );
}

function WorkflowRow({ job, active, onClick }) {
  const cancelPending = isCancelPending(job);
  return (
    <button onClick={onClick} style={{
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 1fr) auto',
      gap: 12,
      alignItems: 'center',
      padding: '12px 14px',
      textAlign: 'left',
      border: `1px solid ${active ? PALETTE.accentSoft : PALETTE.lineSoft}`,
      borderRadius: 10,
      background: active ? PALETTE.accentTint : PALETTE.surface,
      color: PALETTE.ink,
      cursor: 'pointer',
      minWidth: 0,
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, marginBottom: 4 }}>
          <span style={{ fontSize: 13, fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.currentStep || job.jobType || 'workflow'}</span>
          <Pill tone={statusTone(job.status, job.cancelRequested)} style={{ padding: '2px 7px', fontSize: 10 }}>{recoveryStatusLabel(job)}</Pill>
        </div>
        <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: PALETTE.ink3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{job.id}</div>
      </div>
      <IconChevron size={14} />
    </button>
  );
}

function WorkflowDetail({
  job, canRetry, canCancel, onRetry, onCancel, actionStatus,
  canProvidePresentation = false, onProvidePresentation,
}) {
  const error = job.error || {};
  const cancelPending = isCancelPending(job);
  return (
    <div style={{ display: 'grid', gap: 14 }}>
      <div style={detailGridStyle}>
        <DetailItem label="Status" value={cancelPending ? 'Cancel requested' : recoveryStatusLabel(job)} />
        <DetailItem label="Step" value={job.currentStep || '-'} />
        <DetailItem label="Progress" value={`${Number(job.progressPercent || 0)}%`} />
        <DetailItem label="Started" value={fmtDateTime(job.startedAt || job.createdAt)} />
        <DetailItem label="Updated" value={fmtDateTime(job.updatedAt)} />
        <DetailItem label="Finished" value={fmtDateTime(job.finishedAt)} />
      </div>

      <div style={{
        padding: 14,
        border: `1px solid ${PALETTE.lineSoft}`,
        borderRadius: 10,
        background: error.message ? '#FFF8EA' : PALETTE.surfaceAlt,
      }}>
        <div style={{ fontSize: 11, color: PALETTE.ink3, textTransform: 'uppercase', letterSpacing: 0, marginBottom: 6 }}>Safe error summary</div>
        <div style={{ fontSize: 13, color: error.message ? PALETTE.warn : PALETTE.ink3, lineHeight: 1.5 }}>
          {error.message || 'No safe failure summary recorded.'}
        </div>
        {error.code && <div style={{ marginTop: 6, fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: PALETTE.ink3 }}>{error.code}</div>}
      </div>

      {canProvidePresentation && (
        <div style={{
          padding: 14,
          border: `1px solid ${PALETTE.accentSoft || PALETTE.line}`,
          borderRadius: 10,
          background: PALETTE.accentTint || PALETTE.surfaceAlt,
          display: 'grid',
          gap: 10,
        }}>
          <div style={{ fontSize: 13, color: PALETTE.ink, lineHeight: 1.5 }}>
            This funnel needs its presentation details before it can generate. Provide them, then the workflow retries automatically.
          </div>
          <div>
            <Button variant="accent" size="sm" onClick={onProvidePresentation} disabled={actionStatus !== 'idle'}>
              <IconSettings size={13} /> Provide funnel presentation
            </Button>
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <Button
          variant="accent"
          size="sm"
          onClick={onRetry}
          disabled={!canRetry || actionStatus !== 'idle'}
        >
          <IconPlay size={13} /> {actionStatus === 'retry' ? 'Requesting' : 'Retry'}
        </Button>
        <Button
          variant="danger"
          size="sm"
          onClick={onCancel}
          disabled={!canCancel || actionStatus !== 'idle'}
        >
          <IconPause size={13} /> {actionStatus === 'cancel' ? 'Requesting' : 'Cancel'}
        </Button>
      </div>
    </div>
  );
}

function DetailItem({ label, value }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, color: PALETTE.ink3, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, color: PALETTE.ink, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
    </div>
  );
}

function AuditRow({ entry }) {
  return (
    <div style={{
      padding: '11px 12px',
      border: `1px solid ${PALETTE.lineSoft}`,
      borderRadius: 10,
      background: PALETTE.surface,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 5 }}>
        <div style={{ fontSize: 13, color: PALETTE.ink, fontWeight: 700 }}>{entry.action}</div>
        <div style={{ fontFamily: 'JetBrains Mono, monospace', fontSize: 11, color: PALETTE.ink3 }}>{fmtDateTime(entry.createdAt)}</div>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {Object.entries(entry.metadata || {}).slice(0, 6).map(([key, value]) => (
          <span key={key} style={{
            fontSize: 11,
            color: PALETTE.ink2,
            background: PALETTE.surfaceAlt,
            border: `1px solid ${PALETTE.lineSoft}`,
            borderRadius: 7,
            padding: '3px 6px',
          }}>
            {key}: {String(value)}
          </span>
        ))}
      </div>
    </div>
  );
}

function EmptyState({ title, body, compact = false, tone = 'neutral' }) {
  return (
    <div style={{
      padding: compact ? 16 : 28,
      border: `1px solid ${PALETTE.lineSoft}`,
      borderRadius: 10,
      background: tone === 'warn' ? '#FFF8EA' : PALETTE.surface,
      color: tone === 'warn' ? PALETTE.warn : PALETTE.ink3,
    }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: PALETTE.ink, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 13, lineHeight: 1.55 }}>{body}</div>
    </div>
  );
}

const panelStyle = {
  borderRadius: 10,
  boxShadow: '0 1px 2px rgba(26,23,20,0.03)',
};

const summaryGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 180px), 1fr))',
  gap: 14,
};

const mainGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))',
  gap: 18,
  alignItems: 'start',
  marginTop: 18,
};

const detailGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
  gap: 12,
};
